/**
 * History Query Policy
 *
 * Fetches completed/canceled/errored batch processes for the history tab.
 * Uses server-side filtering, sorting, and pagination via readmodel query params.
 * Re-fetches when the user changes page or when active processes complete.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { BatchProcessesListState } from '../state';
import { BatchProcessesListCommand } from '../commands';
import { getBatchProcessesSharedState } from '../../../shared/state/store';

const HISTORY_FILTER = [
  { field: 'status', op: 'in', value: [ 'completed', 'canceled', 'errored' ]}
];

const fetchHistory = async (
  backend: ClientLibraryType,
  dispatchFn: ( cmd: BatchProcessesListCommand, meta: { source: string }) => void,
  page: number,
  perPage: number
) => {
  dispatchFn(
    { type: 'batchProcesses/list/loading/setHistoryLoading', payload: { isLoading: true }},
    { source: 'api' }
  );

  try {
    const [ countResult, processes ] = await Promise.all([
      backend.queries.BatchProcesses({
        filter: HISTORY_FILTER,
        select: ['id']
      }),
      backend.queries.BatchProcesses({
        filter: HISTORY_FILTER,
        sort: [{ field: 'updatedAt', direction: 'desc' }],
        limit: perPage,
        offset: ( page - 1 ) * perPage
      })
    ]);

    dispatchFn(
      { type: 'batchProcesses/list/data/setHistory', payload: { processes: processes ?? [], count: ( countResult ?? []).length }},
      { source: 'api' }
    );
  } catch ( error ) {
    console.error( 'Error fetching batch process history:', error );
    dispatchFn(
      { type: 'batchProcesses/list/loading/setHistoryLoading', payload: { isLoading: false }},
      { source: 'api' }
    );
  }
};

export const createHistoryQueryPolicy = (
  backend: ClientLibraryType
): Policy<BatchProcessesListState, BatchProcessesListCommand> => ({
  name: 'history-query',

  init: ({ dispatch, getState }) => {
    const state = getState();
    fetchHistory( backend, dispatch, state.currentPage, state.perPage );

    // Watch the shared store for completed processes — re-fetch when active count decreases
    let prevActiveCount = getBatchProcessesSharedState().activeProcesses.length;

    const checkInterval = setInterval(() => {
      const currentCount = getBatchProcessesSharedState().activeProcesses.length;
      if ( currentCount < prevActiveCount ) {
        const s = getState();
        fetchHistory( backend, dispatch, s.currentPage, s.perPage );
      }
      prevActiveCount = currentCount;
    }, 2000 );

    return () => {
      clearInterval( checkInterval );
    };
  },

  shouldReact: ( prev, next ) =>
    prev.currentPage !== next.currentPage || prev.perPage !== next.perPage,

  onStateChange: ({ next, ctx }) => {
    fetchHistory( backend, ctx.dispatch, next.currentPage, next.perPage );
  }
});
